<template>
  <template v-if="isContainerLayout && headerAboveCard && containerTitle">
    <v-row align="center" class="vcard-header-local pb-0 mb-2 flex-shrink-0">
      <v-col>
        <h2
          :class="
            containerTitleClass || 'text-h6 font-weight-bold text-secondary'
          "
        >
          {{ containerTitle }}
        </h2>
      </v-col>
      <v-col v-if="containerLinkText" align="right">
        <a
          :class="
            containerLinkClass || 'text-primary font-weight-bold text-uppercase'
          "
          @click="$emit('containerLinkClick')"
        >
          {{ containerLinkText }}
          <v-icon v-if="containerLinkIcon" small aria-hidden="true">{{
            containerLinkIcon
          }}</v-icon>
        </a>
      </v-col>
    </v-row>
  </template>
  <v-card :class="[cardClass, 'p-0 mb-0', 'gateway-status-card-local']">
    <template v-if="isContainerLayout">
      <div class="pa-4">
        <!-- Container Header (inside card, only if not headerAboveCard) -->
        <template v-if="!headerAboveCard && containerTitle">
          <v-row
            align="center"
            class="vcard-header-local pb-0 mb-2 flex-shrink-0"
          >
            <v-col>
              <h2
                :class="
                  containerTitleClass ||
                  'text-h6 font-weight-bold text-secondary'
                "
              >
                {{ containerTitle }}
              </h2>
            </v-col>
            <v-col v-if="containerLinkText" align="right">
              <a
                v-if="containerLinkText"
                :class="
                  containerLinkClass ||
                  'text-primary font-weight-bold text-uppercase'
                "
                @click="$emit('containerLinkClick')"
              >
                {{ containerLinkText }}
                <v-icon v-if="containerLinkIcon" small aria-hidden="true">{{
                  containerLinkIcon
                }}</v-icon>
              </a>
            </v-col>
          </v-row>
        </template>
        <div class="slot-grow-wrapper">
          <slot />
        </div>
      </div>
    </template>
    <template v-else>
      <!-- Item Layout (Original Content) -->
      <v-row no-gutters class="px-3 pt-3">
        <v-col cols="12">
          <div class="card-header mb-2">
            <h5 class="font-weight-bold">{{ gatewayTitle }}</h5>
            <router-link
              v-if="reportSlug"
              :class="
                itemOverviewLinkClass ||
                'text-primary font-weight-bold text-uppercase'
              "
              :to="overviewLinkRoute"
            >
              Overview
            </router-link>
          </div>
        </v-col>
      </v-row>
      <v-row no-gutters class="px-3 pb-3">
        <v-col cols="8" align-self="end">
          <h5 class="font-weight-regular">{{ description }}</h5>
        </v-col>
        <v-col cols="4" class="text-right" align-self="end">
          <h1>{{ formattedScore }}</h1>
        </v-col>
      </v-row>
    </template>

    <!-- Common Footer -->
    <v-row
      v-if="displayFooterText"
      no-gutters
      :class="{ 'mt-auto flex-shrink-0': isContainerLayout && $slots.default }"
    >
      <v-col cols="12">
        <div class="card-footer">
          <span class="card-footer-text">{{ displayFooterText }}</span>
        </div>
      </v-col>
    </v-row>
  </v-card>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { useRoute } from 'vue-router';
  import { mapStatusText, formatGatewayScore } from '../../lib/status-utils';

  interface Props {
    color?: 'green' | 'orange' | 'red' | 'grey';
    isContainerLayout?: boolean;
    headerAboveCard?: boolean;
    containerTitle?: string;
    containerTitleClass?: string;
    containerLinkText?: string;
    containerLinkIcon?: string;
    containerLinkClass?: string;
    containerFooterText?: string;
    gatewayTitle?: string;
    reportSlug?: string;
    itemOverviewLinkClass?: string;
    description?: string;
    score?: string;
    reviewStatusSlug?: string;
    itemFooterText?: string;
  }

  const props = defineProps<Props>();
  const route = useRoute();

  const emit = defineEmits(['containerLinkClick']);

  const cardClass = computed(() => (props.color ? `${props.color}-card` : ''));

  const overviewLinkRoute = computed(() => {
    if (!props.reportSlug) return {};

    // Extract gateway number and grade info from title if available
    const gatewayMatch = props.gatewayTitle?.match(
      /Gateway (\d+)(?:\s*\((.*?)\))?/,
    );
    const gatewayNumber = gatewayMatch ? gatewayMatch[1] : null;
    const gradeFromTitle = gatewayMatch ? gatewayMatch[2] : null;
    const gatewayWord = gatewayNumber
      ? ['one', 'two', 'three', 'four', 'five'][parseInt(gatewayNumber) - 1]
      : null;

    // For Gateway 1, we need to handle grade-specific variants
    const isGatewayOne = gatewayNumber === '1' || gatewayWord === 'one';
    const gradeSlug = gradeFromTitle?.toLowerCase().replace(/\s+/g, '-');
    const gatewaySlug =
      isGatewayOne && gradeSlug
        ? `gateway-one__${gradeSlug}`
        : gatewayWord
          ? `gateway-${gatewayWord}`
          : 'gateway-one';

    // Check if we're on a ReportGrade page
    if (route.name === 'report-grade-overview' && route.params.grade) {
      // Link to the appropriate gateway for the current grade
      return {
        name: 'report-gateway',
        params: {
          id: props.reportSlug,
          grade: route.params.grade,
          gateway: gatewaySlug,
        },
      };
    }

    // Check if we're on a ReportGateway page
    if (route.name === 'report-gateway' && route.params.grade) {
      // Stay in the same grade but link to the appropriate gateway
      return {
        name: 'report-gateway',
        params: {
          id: props.reportSlug,
          grade: route.params.grade,
          gateway: gatewaySlug,
        },
      };
    }

    // Default to report overview
    return {
      name: 'report-overview',
      params: { id: props.reportSlug },
    };
  });

  const displayFooterText = computed(() => {
    const text = props.isContainerLayout
      ? props.containerFooterText
      : props.itemFooterText;
    return mapStatusText(text);
  });

  const formattedScore = computed(() => {
    return formatGatewayScore(props.score, props.reviewStatusSlug);
  });
</script>

<style scoped lang="scss">
  .gateway-status-card-local {
    border-radius: 4px;
    overflow: hidden;
    display: flex;
    flex-direction: column;

    // Define missing CSS variables using existing theme colors
    --card-bg-green: rgb(var(--v-theme-card-bg-green, 235, 245, 241));
    --card-border-green: rgb(var(--v-theme-card-border-green, 179, 217, 200));
    --card-primary-green: rgb(var(--v-theme-status-green, 11, 135, 77));

    --card-bg-orange: rgb(var(--v-theme-light-yellow, 253, 241, 215));
    --card-border-orange: rgb(var(--v-theme-yellow, 241, 167, 6));
    --card-primary-orange: rgb(var(--v-theme-status-yellow, 206, 128, 3));

    --card-bg-red: rgb(var(--v-theme-light-red, 253, 227, 214));
    --card-border-red: rgb(var(--v-theme-red, 245, 78, 0));
    --card-primary-red: rgb(var(--v-theme-status-red, 199, 42, 76));

    --card-border-grey: rgb(var(--v-theme-status-gray, 209, 209, 209));
    --card-primary-grey: rgb(var(--v-theme-status-gray, 209, 209, 209));

    // Default CSS variables for internal elements (can be overridden by color classes)
    --status-card-item-h1-color: inherit;
    --status-card-item-footer-bg: transparent;
    --status-card-item-footer-shadow: transparent;
    --status-card-item-footer-text-color: inherit;

    h1,
    h2,
    h5 {
      margin: 0;
    }

    h1 {
      font-size: 2.2rem;
      line-height: 1.1;
      font-weight: 600;
      color: var(--status-card-item-h1-color) !important;
      white-space: nowrap;
    }

    h5 {
      padding-right: 4px;
      font-size: 0.875rem;
    }

    .vcard-header-local {
      border-bottom: none;
    }

    .card-header {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    }

    :deep(.vcard-header-local a) {
      cursor: pointer !important;
    }

    :deep(a[class*='text-primary']) {
      cursor: pointer !important;
    }

    :deep(a[class*='font-weight-bold']) {
      cursor: pointer !important;
    }

    .vcard-header-local a {
      cursor: pointer !important;
    }

    .vcard-header-local v-col a {
      cursor: pointer !important;
    }

    .gateway-status-card-local a[class*='text-primary'] {
      cursor: pointer !important;
    }

    a,
    .router-link {
      cursor: pointer !important;
    }

    .slot-grow-wrapper {
      flex-grow: 1;
      display: flex;
      flex-direction: column;
    }

    .card-footer {
      padding: 2px 12px;
      text-align: left;
      background-color: var(--status-card-item-footer-bg) !important;
      box-shadow: 0 0 0 1px var(--status-card-item-footer-shadow) !important;
      border-radius: 0 0 3px 3px !important;

      .card-footer-text {
        font-size: 12px;
        font-weight: bold;
        text-transform: uppercase;
        color: var(--status-card-item-footer-text-color) !important;
      }
    }

    // Color-specific styles: Set main background/border and CSS variables
    &.green-card {
      background-color: var(--card-bg-green) !important;
      border: 1px solid var(--card-border-green) !important;
      --status-card-item-h1-color: var(--card-primary-green);
      --status-card-item-footer-bg: var(--card-primary-green);
      --status-card-item-footer-shadow: var(--card-primary-green);
      --status-card-item-footer-text-color: white;
      .vcard-header-local {
        border-bottom: none;
      }
    }

    &.orange-card {
      background-color: var(--card-bg-orange) !important;
      border: 1px solid var(--card-border-orange) !important;
      --status-card-item-h1-color: var(--card-primary-orange);
      --status-card-item-footer-bg: var(--card-primary-orange);
      --status-card-item-footer-shadow: var(--card-primary-orange);
      --status-card-item-footer-text-color: white;
      .vcard-header-local {
        border-bottom: none;
      }
    }

    &.grey-card {
      background-color: white !important;
      border: 1px solid var(--card-border-grey) !important;
      --status-card-item-h1-color: var(--card-primary-grey);
      --status-card-item-footer-bg: var(--card-primary-grey);
      --status-card-item-footer-shadow: var(--card-primary-grey);
      --status-card-item-footer-text-color: rgb(var(--v-theme-tonal-black));
      .vcard-header-local {
        border-bottom: none;
      }
    }

    &.red-card {
      background-color: var(--card-bg-red) !important;
      border: 1px solid var(--card-border-red) !important;
      --status-card-item-h1-color: var(--card-primary-red);
      --status-card-item-footer-bg: var(--card-primary-red);
      --status-card-item-footer-shadow: var(--card-primary-red);
      --status-card-item-footer-text-color: white;
    }
  }
</style>
